import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  Scene,
} from 'three';
import { BLOCK_STEP, CITY, HALF_CITY, PALETTE, POPULATION } from '../config/settings';
import { Updatable } from '../core/types';
import { chance, pick, randFloat } from '../utils/random';

interface Car {
  readonly mesh: Group;
  readonly horizontal: boolean;
  readonly direction: number; // +1 or -1
  readonly speed: number;
}

/** Spawns cartoon cars that drive down the avenues and wrap around the city. */
export class TrafficSystem implements Updatable {
  private readonly cars: Car[] = [];
  private readonly bound = HALF_CITY + 30;
  private static readonly GROUND_Y = 0.07;

  constructor(scene: Scene) {
    const lanes = this.laneCentres();
    for (let i = 0; i < POPULATION.CARS; i++) {
      const car = this.spawnCar(lanes);
      scene.add(car.mesh);
      this.cars.push(car);
    }
  }

  update(dt: number): void {
    for (const car of this.cars) {
      const step = car.speed * car.direction * dt;
      const axis = car.horizontal ? 'x' : 'z';
      car.mesh.position[axis] += step;
      if (car.mesh.position[axis] > this.bound) car.mesh.position[axis] = -this.bound;
      else if (car.mesh.position[axis] < -this.bound) car.mesh.position[axis] = this.bound;
    }
  }

  private laneCentres(): number[] {
    const lanes: number[] = [];
    for (let i = 0; i < CITY.GRID; i++) {
      lanes.push(-HALF_CITY + CITY.ROAD_WIDTH / 2 + i * BLOCK_STEP - CITY.ROAD_WIDTH / 2);
    }
    return lanes;
  }

  private spawnCar(lanes: number[]): Car {
    const horizontal = chance(0.5);
    const lane = pick(lanes) + (chance(0.5) ? -5 : 5);
    const direction = chance(0.5) ? 1 : -1;
    const along = randFloat(-HALF_CITY, HALF_CITY);
    const mesh = this.createCarMesh();

    if (horizontal) {
      mesh.position.set(along, TrafficSystem.GROUND_Y, lane);
      mesh.rotation.y = direction > 0 ? Math.PI / 2 : -Math.PI / 2;
    } else {
      mesh.position.set(lane, TrafficSystem.GROUND_Y, along);
      mesh.rotation.y = direction > 0 ? 0 : Math.PI;
    }
    return { mesh, horizontal, direction, speed: randFloat(14, 26) };
  }

  private createCarMesh(): Group {
    const car = new Group();
    const body = new Mesh(new BoxGeometry(3.2, 1.1, 6.4), new MeshLambertMaterial({ color: pick(PALETTE.cars) }));
    body.position.y = 1.0;
    body.castShadow = true;
    car.add(body);

    const cabin = new Mesh(new BoxGeometry(2.8, 1.0, 3.0), new MeshLambertMaterial({ color: 0x99c7ff }));
    cabin.position.set(0, 1.85, -0.3);
    cabin.castShadow = true;
    car.add(cabin);

    const wheelGeo = new CylinderGeometry(0.7, 0.7, 0.5, 10);
    const wheelMat = new MeshLambertMaterial({ color: 0x111317 });
    for (const sx of [-1.4, 1.4]) {
      for (const sz of [-2.1, 2.1]) {
        const wheel = new Mesh(wheelGeo, wheelMat);
        wheel.rotation.z = Math.PI / 2;
        wheel.position.set(sx, 0.6, sz);
        car.add(wheel);
      }
    }
    car.scale.setScalar(0.72); // proportional to human-scale pedestrians
    return car;
  }
}
